import React from "react";
import { Box, IconButton, Typography, SxProps, Theme } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { DASHBOARD, dashboardIconButtonSx } from "./dashboardTheme";

/** Text action for accordion headers — span + role=button avoids nesting inside AccordionSummary's button. */
export function DashboardTextActionLink({
  children,
  onClick,
  sx,
}: {
  children: React.ReactNode;
  onClick: (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
  sx?: SxProps<Theme>;
}) {
  const handleActivate = (
    e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
  ) => {
    e.stopPropagation();
    onClick(e);
  };
  return (
    <Typography
      component="span"
      role="button"
      tabIndex={0}
      onClick={handleActivate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleActivate(e);
        }
      }}
      sx={{
        border: "none",
        background: "none",
        cursor: "pointer",
        color: DASHBOARD.accent,
        fontSize: 14,
        fontWeight: 500,
        fontFamily: "inherit",
        p: 0,
        "&:hover": { textDecoration: "underline" },
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

export function DashboardSectionHeader({
  icon,
  title,
  subtitle,
  onAction,
  actionLabel,
  infoTooltip,
}: {
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  onAction?: () => void;
  actionLabel?: string;
  /** Shown beside the title (e.g. InfoOutlined tooltip). */
  infoTooltip?: React.ReactNode;
}) {
  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", width: "100%" }}>
      {icon ? (
        <Box sx={{ color: DASHBOARD.header, mt: 0.25, display: "flex" }}>{icon}</Box>
      ) : null}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.25 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: DASHBOARD.header, lineHeight: 1.3 }}>
            {title}
          </Typography>
          {infoTooltip}
        </Box>
        {subtitle ? (
          <Typography sx={{ fontSize: 13, color: DASHBOARD.text, mt: 0.25 }}>{subtitle}</Typography>
        ) : null}
      </Box>
      {onAction && actionLabel ? (
        <DashboardTextActionLink onClick={() => onAction()}>
          {actionLabel}
        </DashboardTextActionLink>
      ) : null}
    </Box>
  );
}

export function DashboardMetricActionButton({ onClick }: { onClick?: () => void }) {
  return (
    <IconButton
      component="span"
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      sx={dashboardIconButtonSx}
      aria-label="Open"
    >
      <OpenInNewIcon sx={{ fontSize: 16 }} />
    </IconButton>
  );
}
